// Sprint auto-failover-on-rate-limit (D013-EC-109) — renamed from
// `RunnerOfflineBanner.tsx` (S20.3 PR-6) and extended to two variants.
//
// Displayed inline above the composer when the most recent send rejected
// with a backend code the user can recover from by reassigning their
// conversation to a different runner. Owns two affordances, identical
// across variants:
//
//   - "Try another runner" → POSTs `/api/agents/:id/failover`. On success
//     the backend has flipped `runner_id`, persisted a system message
//     (`🔁 Reasignado…`), and respawned via `--resume`. The banner
//     clears + the toast confirms which runner picked up.
//   - "Dismiss" (×) → silent local close. The banner re-appears on the
//     next failed send.
//
// Two variants discriminate by the failure CLASS, not the recovery
// (which is identical for both):
//
//   - `offline`       → `RUNNER_OFFLINE` 503 from send. Red palette
//                       (`status-error/*`). Copy: "{runner} is offline".
//   - `rate_limited`  → `RUNNER_RATE_LIMITED` 503 from send. Warning
//                       palette (`status-warning/*`). Copy: "{runner}
//                       reached its weekly quota". Plain language — never
//                       "rate-limit" or "weekly cap" in the UI per
//                       founder-first-vision §5.
//
// Why one component with a variant instead of two siblings: the recovery
// surface IS the same (one button, one POST, one toast). Two components
// would duplicate the click handler + ARIA wiring + busy-state logic and
// introduce drift the moment one variant adds a new affordance.

import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { useAppStore } from '../../stores/app.store';
import { useToastStore } from '../../stores/toast.store';
import { failoverAgentRequest } from '../../lib/api';

export type RunnerUnavailableVariant = 'offline' | 'rate_limited';

export interface RunnerUnavailableBannerProps {
  agentId: string;
  runnerId: string;
  lastErrorMessage: string;
  variant: RunnerUnavailableVariant;
}

// Token bundles per variant. Kept inline because (a) the two variants
// are the entire API of this component, (b) lint forbids `bg-[#hex]` so
// every token here is a semantic Tailwind class wired in tailwind.config.js
// (`status-error`, `status-warning`).
const TOKENS: Record<RunnerUnavailableVariant, {
  container: string;
  titleText: string;
  button: string;
  dismissRing: string;
}> = {
  offline: {
    container: 'border-status-error/30 bg-status-error/10',
    titleText: 'text-status-error',
    button:
      'bg-status-error/20 text-status-error hover:bg-status-error/30 focus:ring-status-error/40',
    dismissRing: 'focus:ring-status-error/40',
  },
  rate_limited: {
    container: 'border-status-warning/30 bg-status-warning/10',
    titleText: 'text-status-warning',
    button:
      'bg-status-warning/20 text-status-warning hover:bg-status-warning/30 focus:ring-status-warning/40',
    dismissRing: 'focus:ring-status-warning/40',
  },
};

export function RunnerUnavailableBanner({
  agentId,
  runnerId,
  variant,
}: RunnerUnavailableBannerProps) {
  const { t } = useTranslation();
  const clearRunnerUnavailable = useAppStore((s) => s.clearRunnerUnavailable);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const [busy, setBusy] = useState(false);

  const tokens = TOKENS[variant];
  // i18n bundle namespaces the per-variant copy under
  // `runnerUnavailableBanner.{offline,rateLimited}.*`; shared copy lives
  // at `runnerUnavailableBanner.*` (busy label, toasts, dismiss).
  const variantKey = variant === 'offline' ? 'offline' : 'rateLimited';

  const handleFailover = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await failoverAgentRequest(agentId);
      upsertAgent(res.agent);
      clearRunnerUnavailable(agentId);
      useToastStore.getState().push({
        message: t('runnerUnavailableBanner.successToast', {
          to: res.failover.toHostname,
        }),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('runnerUnavailableBanner.genericError');
      useToastStore.getState().push({
        message: t('runnerUnavailableBanner.errorToast', { reason: message }),
        durationMs: 8_000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="runner-unavailable-banner"
      data-variant={variant}
      data-runner-id={runnerId}
      className={`mx-3 mt-2 mb-2 flex items-start justify-between gap-3 rounded-md border ${tokens.container} px-3 py-2 text-body text-foreground`}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <span className={`${tokens.titleText} font-medium`}>
          {t(`runnerUnavailableBanner.${variantKey}.title`, { runner: runnerId })}
        </span>
        <span className="text-foreground/80 text-caption">
          {t(`runnerUnavailableBanner.${variantKey}.subtitle`)}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={handleFailover}
          disabled={busy}
          data-testid="runner-unavailable-failover-btn"
          className={`px-3 py-1 rounded-md ${tokens.button} disabled:opacity-60 text-caption font-medium transition-colors focus:outline-none focus:ring-2`}
        >
          {busy
            ? t('runnerUnavailableBanner.busyLabel')
            : t(`runnerUnavailableBanner.${variantKey}.tryAnotherRunner`)}
        </button>
        <button
          type="button"
          onClick={() => clearRunnerUnavailable(agentId)}
          aria-label={t('runnerUnavailableBanner.dismissAria')}
          data-testid="runner-unavailable-dismiss"
          className={`text-foreground/60 hover:text-foreground transition-colors focus:outline-none focus:ring-2 ${tokens.dismissRing} rounded-sm px-1`}
        >
          {t('runnerUnavailableBanner.dismissGlyph')}
        </button>
      </div>
    </div>
  );
}
